import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ArrowLeft, Clock, Briefcase, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExploreGigDetail = () => {
  const { gigId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/gigs/${gigId}`).then(r => setData(r.data)).catch(() => navigate('/explore')).finally(() => setLoading(false));
  }, [gigId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return null;
  const { gig, freelancer } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/explore" className="font-heading font-bold text-xl text-primary">TaskPiece</a>
          <div className="flex gap-2"><Button variant="ghost" onClick={() => navigate('/login')}>Log In</Button><Button onClick={() => navigate('/signup')}>Sign Up</Button></div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Button variant="ghost" onClick={() => navigate('/explore')} className="mb-6" data-testid="back-explore"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Explore</Button>
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="flex items-center gap-2">
              {gig.task_code && <code className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-mono">{gig.task_code}</code>}
              <Badge variant="outline">{gig.category}</Badge>
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-testid="gig-title">{gig.title}</h1>
            <p className="text-muted-foreground leading-relaxed">{gig.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><span className="text-xs text-muted-foreground">Price</span><p className="font-heading font-bold text-secondary text-xl">₹{gig.price?.toLocaleString('en-IN')}</p></div>
              <div><span className="text-xs text-muted-foreground">Delivery</span><p className="font-bold flex items-center gap-1"><Clock className="w-4 h-4" /> {gig.delivery_days} days</p></div>
              <div><span className="text-xs text-muted-foreground">Rating</span><p className="font-bold flex items-center gap-1"><Star className="w-4 h-4 fill-amber-500 text-amber-500" /> {gig.rating?.toFixed(1)}</p></div>
              <div><span className="text-xs text-muted-foreground">Orders</span><p className="font-bold">{gig.orders_count}</p></div>
            </div>
            {gig.tags?.length > 0 && <div className="flex gap-1.5 flex-wrap">{gig.tags.map(t => <Badge key={t} variant="secondary">{t}</Badge>)}</div>}
            {freelancer && (
              <div className="pt-4 border-t flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">{freelancer.full_name?.charAt(0)}</div>
                <div>
                  <p className="font-bold">{freelancer.full_name}</p>
                  <p className="text-xs text-muted-foreground">{freelancer.completed_jobs || 0} jobs · {freelancer.rating?.toFixed(1)} rating</p>
                </div>
              </div>
            )}
            <Button className="w-full md:w-auto" onClick={() => navigate('/signup')} data-testid="gig-signup-cta">
              Sign Up to Order This Gig <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ExploreProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/projects/${projectId}`).then(r => setData(r.data)).catch(() => navigate('/explore')).finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return null;
  const { project } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/explore" className="font-heading font-bold text-xl text-primary">TaskPiece</a>
          <div className="flex gap-2"><Button variant="ghost" onClick={() => navigate('/login')}>Log In</Button><Button onClick={() => navigate('/signup')}>Sign Up</Button></div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Button variant="ghost" onClick={() => navigate('/explore')} className="mb-6" data-testid="back-explore"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Explore</Button>
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="flex items-center gap-2">
              {project.task_code && <code className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-mono">{project.task_code}</code>}
              <Badge variant="outline">{project.category}</Badge>
              <Badge>{project.status}</Badge>
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-testid="project-title">{project.title}</h1>
            <p className="text-muted-foreground leading-relaxed">{project.description}</p>
            <div className="grid grid-cols-2 gap-4">
              <div><span className="text-xs text-muted-foreground">Budget</span><p className="font-heading font-bold text-secondary text-xl">₹{project.budget_min?.toLocaleString('en-IN')} - ₹{project.budget_max?.toLocaleString('en-IN')}</p></div>
              <div><span className="text-xs text-muted-foreground">Bids</span><p className="font-heading font-bold text-xl">{project.bids_count}</p></div>
            </div>
            {project.skills_required?.length > 0 && <div className="flex gap-1.5 flex-wrap">{project.skills_required.map(s => <Badge key={s} variant="secondary">{s}</Badge>)}</div>}
            <Button className="w-full md:w-auto" onClick={() => navigate('/signup')} data-testid="project-signup-cta">
              Sign Up to Bid on This Project <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ExploreFreelancerDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/freelancers/${userId}`).then(r => setData(r.data)).catch(() => navigate('/explore')).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return null;
  const { user, reviews, gigs } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/explore" className="font-heading font-bold text-xl text-primary">TaskPiece</a>
          <div className="flex gap-2"><Button variant="ghost" onClick={() => navigate('/login')}>Log In</Button><Button onClick={() => navigate('/signup')}>Sign Up</Button></div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Button variant="ghost" onClick={() => navigate('/explore')} className="mb-6" data-testid="back-explore"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Explore</Button>

        {/* Profile Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-5">
              <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-heading font-bold flex-shrink-0">
                {user.full_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-heading font-bold text-primary mb-1" data-testid="fl-name">{user.full_name}</h1>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-500 text-amber-500" /><span className="font-bold">{user.rating?.toFixed(1)}</span><span className="text-sm text-muted-foreground">({user.total_reviews} reviews)</span></div>
                  {user.verified && <Badge variant="outline">Verified</Badge>}
                </div>
                <p className="text-muted-foreground mb-3">{user.bio || 'Freelancer on TaskPiece'}</p>
                <div className="flex gap-1.5 flex-wrap mb-3">
                  {(user.skills || []).map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}
                </div>
                <div className="text-sm text-muted-foreground"><Briefcase className="w-3.5 h-3.5 inline mr-1" /> {user.completed_jobs || 0} jobs completed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Gigs */}
        {gigs.length > 0 && (
          <Card className="mb-6">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-4">Active Gigs ({gigs.length})</h2>
              <div className="space-y-3">
                {gigs.map(g => (
                  <div key={g.id} className="flex justify-between items-center py-2 border-b last:border-0 cursor-pointer hover:bg-slate-50 rounded px-2 -mx-2" onClick={() => navigate(`/explore/gig/${g.id}`)}>
                    <div>
                      <p className="font-medium">{g.title}</p>
                      <p className="text-xs text-muted-foreground">{g.category} · {g.delivery_days}d delivery</p>
                    </div>
                    <span className="font-heading font-bold text-secondary">₹{g.price?.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        {reviews.length > 0 && (
          <Card className="mb-6">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-4">Reviews ({reviews.length})</h2>
              <div className="space-y-4">
                {reviews.map(r => (
                  <div key={r.id} className="border-b pb-3 last:border-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-500 text-amber-500' : 'text-slate-200'}`} />)}</div>
                      <span className="text-sm font-medium">{r.reviewer_name}</span>
                      <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{r.comment}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Button className="w-full md:w-auto" onClick={() => navigate('/signup')} data-testid="hire-cta">
          Sign Up to Hire {user.full_name} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
